import { generatePath } from 'react-router-dom';
import type {
    IDeleteDocumentRequest,
    IDocument,
    IFetchDocumentsRequest,
    IFetchVaultsRequest,
    IMediaTypeRead,
    ISecureDocumentStoreSettings,
    ITag,
    IUpdateDocumentAttributesRequest,
    IVault,
} from '../../domain';
import { ApiBaseService } from '../api-base-service';

export const ID_PLACEHOLDER = ':id';
export const VERSION_PLACEHOLDER = ':version';

export const Endpoint = {
    DOCUMENT: `/documents/${ID_PLACEHOLDER}`,
    DOCUMENTS: '/documents',
    DOCUMENTS_VERSION: `/documents/${ID_PLACEHOLDER}/versions`,
    DOCUMENTS_FETCH_VERSION: `/documents/${ID_PLACEHOLDER}/versions/${VERSION_PLACEHOLDER}`,
    DOCUMENTS_READ: `/documents/${ID_PLACEHOLDER}/read`,
    DOCUMENTS_UNREAD: `/documents/${ID_PLACEHOLDER}/unread`,
    VAULT: '/vaults',
    VAULT_DOCUMENT: `/vaults/${ID_PLACEHOLDER}/documents`,
    VAULT_UNREAD_COUNT: `/vaults/${ID_PLACEHOLDER}/documents/unread-count`,
    SETTINGS: '/settings',
    TAGS: '/tags',
    MEDIA_TYPES: '/media-types',
};

export class SecureDocumentStoreApiService extends ApiBaseService {
    constructor(context = '/delegate/secure-document-store/v3') {
        super(context);
    }

    async fetchDocuments(request: IFetchDocumentsRequest): Promise<{ documents: IDocument[]; numberOfDocuments: number }> {
        const tagIds = request['tag-id'] || [];
        const vaultId = request['vault-id'] && request['vault-id'].join(',');
        const params = {
            ...request,
            'vault-id': vaultId,
            'tag-id': tagIds.join(','),
        };

        const { data, headers } = await this.http.get<IDocument[]>(Endpoint.DOCUMENTS, { params });

        return {
            documents: data,
            numberOfDocuments: headers['x-total-count'],
        };
    }

    async fetchDocumentUnreadCount(vaultId: string): Promise<number> {
        const { data } = await this.http.get<number>(generatePath(Endpoint.VAULT_UNREAD_COUNT, { id: vaultId }));

        return data;
    }

    async fetchDocument(documentId: string): Promise<IDocument> {
        const { data } = await this.http.get<IDocument>(generatePath(Endpoint.DOCUMENT, { id: documentId }));

        return data;
    }

    async deleteDocument(request: IDeleteDocumentRequest) {
        const { id, ...rest } = request;
        const params = {
            ...rest,
            'send-notification': true,
        };

        return this.http.delete(generatePath(Endpoint.DOCUMENT, { id }), { params });
    }

    async updateDocument(request: IUpdateDocumentAttributesRequest) {
        const { id, ...params } = request;

        return this.http.put(generatePath(Endpoint.DOCUMENT, { id }), params);
    }

    async fetchVaults(request: IFetchVaultsRequest): Promise<IVault[]> {
        const params = {
            ...request,
            'external-id': request['external-id'] ? request['external-id'].join(',') : undefined,
        };

        const { data } = await this.http.get<IVault[]>(Endpoint.VAULT, { params });

        return data;
    }

    async fetchSettings(): Promise<ISecureDocumentStoreSettings> {
        const { data } = await this.http.get<ISecureDocumentStoreSettings>(Endpoint.SETTINGS);

        return data;
    }

    async fetchTags(): Promise<ITag[]> {
        const { data } = await this.http.get<ITag[]>(Endpoint.TAGS);

        return data;
    }

    async fetchMediaTypes(): Promise<IMediaTypeRead[]> {
        const { data } = await this.http.get<IMediaTypeRead[]>(Endpoint.MEDIA_TYPES);

        return data;
    }

    async uploadDocument(vaultId: string, formData: FormData): Promise<string | undefined> {
        const headerArguments = {
            'Content-Type': 'multipart/form-data',
        };
        const params = {
            'send-notification': true,
        };

        const { headers } = await this.http.post(generatePath(Endpoint.VAULT_DOCUMENT, { id: vaultId }), formData, { headers: headerArguments, params });
        const location = headers.location as string | undefined;
        if (!location) {
            throw new Error('No location header on upload document response');
        }

        return location.split('/').pop(); // the document id of the created document is the last part of the returned location header
    }

    async markDocumentAsRead(documentId: string) {
        return this.http.post(generatePath(Endpoint.DOCUMENTS_READ, { id: documentId }));
    }

    async markDocumentAsUnread(documentId: string) {
        return this.http.post(generatePath(Endpoint.DOCUMENTS_UNREAD, { id: documentId }));
    }
}
